/**
 * One memo section as the recipe describes it: a BRIEF, not a checklist.
 *
 * Collapsed, the card is the section's number, fixed title, the first line of
 * its brief and two facts about it (when it appears, who writes it). Expanded,
 * the brief, "appears" rule and sub-headings are editable; the
 * title is not — the firm's memos share one outline, and a renamed section
 * would be a different memo.
 *
 * The draft lives here and is sent on Save as ONLY the fields that changed:
 * the server merges edits key by key, so an untouched field must not be sent
 * back as if the user had chosen it.
 */

import { ChevronDown, ChevronRight, X } from "lucide-react";
import React, { useEffect, useId, useState } from "react";

import type {
	DealScreeningSectionEdit,
	DealScreeningSectionSpec,
} from "../../types/dealScreening";
import { sentenceCaseHeading } from "./memoModel";

export interface SectionBriefCardProps {
	section: DealScreeningSectionSpec;
	/** 1, 2… for the fixed outline; omitted for a deal-specific section. */
	number?: number;
	expanded: boolean;
	onToggle: () => void;
	/** Resolves true when stored. */
	onSave: (edit: DealScreeningSectionEdit) => Promise<boolean>;
}

const INPUT =
	"mt-1 w-full rounded border border-gray-300 px-2 py-1 text-base text-text-base focus:outline-none focus:ring-1 focus:ring-[#008782]";

/** The brief's first sentence: what the collapsed card can say in one line. */
const firstLine = (brief: string): string => {
	const trimmed = brief.trim();
	const end = trimmed.search(/[.!?](\s|$)/);
	return end === -1 ? trimmed : trimmed.slice(0, end + 1);
};

const APPEARS_LABEL: Record<string, string> = {
	always: "Always",
	if_sourced: "If the files cover it",
};

interface Draft {
	brief: string;
	appears: string;
	sub_headings: string[];
	enabled: boolean;
}

const draftOf = (section: DealScreeningSectionSpec): Draft => ({
	brief: section.brief,
	appears: section.appears,
	sub_headings: section.sub_headings,
	enabled: section.enabled,
});

/** The fields the user actually changed, and nothing else. */
export function changedFields(
	section: DealScreeningSectionSpec,
	draft: Draft,
): DealScreeningSectionEdit {
	const edit: DealScreeningSectionEdit = {};
	if (draft.brief !== section.brief) edit.brief = draft.brief;
	if (draft.appears !== section.appears) edit.appears = draft.appears;
	if (draft.sub_headings.join("\n") !== section.sub_headings.join("\n")) {
		edit.sub_headings = draft.sub_headings;
	}
	if (draft.enabled !== section.enabled) edit.enabled = draft.enabled;
	return edit;
}

const APPEARS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
	{ value: "always", label: "Always" },
	{ value: "if_sourced", label: "Only if the files cover it" },
];

const SectionBriefCard: React.FC<SectionBriefCardProps> = ({
	section,
	number,
	expanded,
	onToggle,
	onSave,
}) => {
	const [draft, setDraft] = useState<Draft>(() => draftOf(section));
	const [newHeading, setNewHeading] = useState("");
	const [saving, setSaving] = useState(false);
	const briefId = useId();
	const headingId = useId();

	// The stored section changed (a save landed): the draft restarts from it.
	useEffect(() => {
		setDraft(draftOf(section));
	}, [section]);

	const edit = changedFields(section, draft);
	const dirty = Object.keys(edit).length > 0;
	const codeWritten = section.written_by === "code";

	const addHeading = () => {
		const heading = newHeading.trim();
		if (!heading || draft.sub_headings.includes(heading)) return;
		setDraft({ ...draft, sub_headings: [...draft.sub_headings, heading] });
		setNewHeading("");
	};

	const save = async () => {
		if (!dirty || saving) return;
		setSaving(true);
		try {
			await onSave(edit);
		} finally {
			setSaving(false);
		}
	};

	const Chevron = expanded ? ChevronDown : ChevronRight;

	return (
		<li
			className={`border-b border-[#f1f2f4] last:border-0 ${expanded ? "bg-[#f6fbfb]" : "bg-white"}`}
			data-testid={`section-card-${section.key}`}
		>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={expanded}
				className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.02]"
			>
				{number !== undefined ? (
					<span
						className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md text-xs font-semibold ${
							expanded ? "bg-[#004C4B] text-white" : "bg-[#f2f2f2] text-[#5B6472]"
						}`}
					>
						{number}
					</span>
				) : (
					<Chevron className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
				)}
				<span className="min-w-0 flex-1">
					<span
						className={`block text-[14px] font-semibold ${
							section.enabled ? "text-text-base" : "text-text-muted line-through"
						}`}
					>
						{section.title}
					</span>
					{!expanded && (
						<span className="mt-0.5 block truncate text-[12.5px] text-[#6B7280]">
							{firstLine(section.brief)}
						</span>
					)}
				</span>
				<span className="shrink-0 rounded-full bg-[#f2f2f2] px-2.5 py-0.5 text-xs font-medium text-[#3f4247]">
					{section.enabled ? (APPEARS_LABEL[section.appears] ?? section.appears) : "Left out"}
				</span>
			</button>

			{expanded && (
				<div className="space-y-3 px-4 pb-4 pl-[50px]">
					<div>
						<label htmlFor={briefId} className="text-[12.5px] font-semibold text-[#3f4247]">
							What this section says
						</label>
						<p className="text-xs text-[#6B7280]">
							Brief Doppler the way you would brief a junior analyst.
						</p>
						<textarea
							id={briefId}
							value={draft.brief}
							onChange={(event) => setDraft({ ...draft, brief: event.target.value })}
							rows={4}
							className={INPUT}
						/>
					</div>

					<div>
						<p className="text-[12.5px] font-semibold text-[#3f4247]">
							Sub-headings, used when the files cover them
						</p>
						<div className="mt-1 flex flex-wrap items-center gap-1.5">
							{draft.sub_headings.map((heading) => (
								<span
									key={heading}
									className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-sm text-text-base"
								>
									{/* Read as the memo prints it (DOP-660): "What They Do" is
									    "What they do". The stored heading is left as it is. */}
									{sentenceCaseHeading(heading)}
									<button
										type="button"
										onClick={() =>
											setDraft({
												...draft,
												sub_headings: draft.sub_headings.filter(
													(existing) => existing !== heading,
												),
											})
										}
										aria-label={`Remove ${sentenceCaseHeading(heading)}`}
										className="text-text-muted hover:text-text-base"
									>
										<X className="h-3 w-3" aria-hidden />
									</button>
								</span>
							))}
							<label htmlFor={headingId} className="sr-only">
								New sub-heading
							</label>
							<input
								id={headingId}
								value={newHeading}
								onChange={(event) => setNewHeading(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault();
										addHeading();
									}
								}}
								placeholder="Add"
								className="w-28 rounded border border-gray-300 px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#008782]"
							/>
						</div>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						<fieldset>
							<legend className="text-[12.5px] font-semibold text-[#3f4247]">When it appears</legend>
							{APPEARS_OPTIONS.map((option) => (
								<label
									key={option.value}
									className="mt-1 flex items-center gap-2 text-sm text-text-base"
								>
									<input
										type="radio"
										name={`appears-${section.key}`}
										checked={draft.appears === option.value}
										onChange={() => setDraft({ ...draft, appears: option.value })}
										className="border-gray-300 text-[#008782] focus:ring-[#008782]"
									/>
									{option.label}
								</label>
							))}
						</fieldset>
					</div>

					<label className="flex items-center gap-2 text-sm text-text-base">
						<input
							type="checkbox"
							checked={draft.enabled}
							onChange={(event) =>
								setDraft({ ...draft, enabled: event.target.checked })
							}
							className="rounded border-gray-300 text-[#008782] focus:ring-[#008782]"
						/>
						Include this section in the memo
					</label>

					<p className="text-xs text-text-muted">
						{codeWritten
							? "Built from the figures Doppler found. Doppler never writes a number itself."
							: "Every sentence in this section links to the page it came from."}
					</p>

					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => void save()}
							disabled={!dirty || saving}
							className="rounded-lg bg-[#004C4B] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
						>
							{saving ? "Saving…" : "Save brief"}
						</button>
						<button
							type="button"
							onClick={() => setDraft(draftOf(section))}
							disabled={!dirty || saving}
							className="rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-text-strong disabled:opacity-50"
						>
							Discard changes
						</button>
					</div>
				</div>
			)}
		</li>
	);
};

export default SectionBriefCard;
